/**
 * Comprehensive Offline Database
 * Full IndexedDB storage for ALL restaurant data
 * Enables complete offline operation with audit logging
 */

const DB_NAME = 'hambre_restaurant_db';
const DB_VERSION = 2;

// All data stores
const STORES = {
  // Core restaurant data
  RESTAURANT: 'restaurant',
  MENU_ITEMS: 'menu_items',
  CATEGORIES: 'categories',
  MODIFIERS: 'modifiers',
  
  // Operations
  ORDERS: 'orders',
  TABLE_SESSIONS: 'table_sessions',
  BOOKINGS: 'bookings',
  
  // Inventory
  STOCK: 'stock',
  STOCK_MOVEMENTS: 'stock_movements',
  
  // Tables & Staff
  TABLES: 'tables',
  STAFF: 'staff',
  WAITERS: 'waiters',
  
  // Customers
  CUSTOMERS: 'customers',
  LOYALTY: 'loyalty',
  
  // Financial
  TRANSACTIONS: 'transactions',
  DAILY_REPORTS: 'daily_reports',
  
  // Sync & Audit
  SYNC_QUEUE: 'sync_queue',
  AUDIT_LOG: 'audit_log',
  CONFLICTS: 'conflicts',
  
  // Meta
  SYNC_STATUS: 'sync_status',
  SETTINGS: 'settings'
};

let db = null;
let isInitialized = false;

/**
 * Initialize the comprehensive offline database
 */
export const initOfflineDB = () => {
  return new Promise((resolve, reject) => {
    if (db && isInitialized) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('OfflineDB: Error opening database:', event.target.error);
      reject(event.target.error);
    };

    request.onsuccess = (event) => {
      db = event.target.result;
      isInitialized = true;
      console.log('OfflineDB: Database initialized successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      
      // Create all stores with appropriate indexes
      createStores(database);
    };
  });
};

/**
 * Create all object stores
 */
function createStores(database) {
  // Restaurant settings
  if (!database.objectStoreNames.contains(STORES.RESTAURANT)) {
    database.createObjectStore(STORES.RESTAURANT, { keyPath: 'id' });
  }

  // Menu items
  if (!database.objectStoreNames.contains(STORES.MENU_ITEMS)) {
    const menuStore = database.createObjectStore(STORES.MENU_ITEMS, { keyPath: 'id' });
    menuStore.createIndex('restaurant_id', 'restaurant_id', { unique: false });
    menuStore.createIndex('category', 'category', { unique: false });
    menuStore.createIndex('is_available', 'is_available', { unique: false });
  }

  // Categories
  if (!database.objectStoreNames.contains(STORES.CATEGORIES)) {
    const catStore = database.createObjectStore(STORES.CATEGORIES, { keyPath: 'id' });
    catStore.createIndex('restaurant_id', 'restaurant_id', { unique: false });
  }

  // Modifiers
  if (!database.objectStoreNames.contains(STORES.MODIFIERS)) {
    const modStore = database.createObjectStore(STORES.MODIFIERS, { keyPath: 'id' });
    modStore.createIndex('restaurant_id', 'restaurant_id', { unique: false });
  }

  // Orders
  if (!database.objectStoreNames.contains(STORES.ORDERS)) {
    const orderStore = database.createObjectStore(STORES.ORDERS, { keyPath: 'id' });
    orderStore.createIndex('restaurant_id', 'restaurant_id', { unique: false });
    orderStore.createIndex('status', 'status', { unique: false });
    orderStore.createIndex('created_at', 'created_at', { unique: false });
    orderStore.createIndex('is_synced', 'is_synced', { unique: false });
    orderStore.createIndex('local_id', 'local_id', { unique: false });
  }

  // Table sessions
  if (!database.objectStoreNames.contains(STORES.TABLE_SESSIONS)) {
    const sessionStore = database.createObjectStore(STORES.TABLE_SESSIONS, { keyPath: 'id' });
    sessionStore.createIndex('restaurant_id', 'restaurant_id', { unique: false });
    sessionStore.createIndex('table_id', 'table_id', { unique: false });
    sessionStore.createIndex('status', 'status', { unique: false });
  }

  // Bookings
  if (!database.objectStoreNames.contains(STORES.BOOKINGS)) {
    const bookingStore = database.createObjectStore(STORES.BOOKINGS, { keyPath: 'id' });
    bookingStore.createIndex('restaurant_id', 'restaurant_id', { unique: false });
    bookingStore.createIndex('date', 'date', { unique: false });
    bookingStore.createIndex('status', 'status', { unique: false });
  }

  // Stock
  if (!database.objectStoreNames.contains(STORES.STOCK)) {
    const stockStore = database.createObjectStore(STORES.STOCK, { keyPath: 'id' });
    stockStore.createIndex('restaurant_id', 'restaurant_id', { unique: false });
    stockStore.createIndex('category', 'category', { unique: false });
    stockStore.createIndex('low_stock', 'low_stock', { unique: false });
  }

  // Stock movements
  if (!database.objectStoreNames.contains(STORES.STOCK_MOVEMENTS)) {
    const movementStore = database.createObjectStore(STORES.STOCK_MOVEMENTS, { keyPath: 'id' });
    movementStore.createIndex('stock_id', 'stock_id', { unique: false });
    movementStore.createIndex('created_at', 'created_at', { unique: false });
    movementStore.createIndex('is_synced', 'is_synced', { unique: false });
  }

  // Tables
  if (!database.objectStoreNames.contains(STORES.TABLES)) {
    const tableStore = database.createObjectStore(STORES.TABLES, { keyPath: 'id' });
    tableStore.createIndex('restaurant_id', 'restaurant_id', { unique: false });
    tableStore.createIndex('is_active', 'is_active', { unique: false });
  }

  // Staff
  if (!database.objectStoreNames.contains(STORES.STAFF)) {
    const staffStore = database.createObjectStore(STORES.STAFF, { keyPath: 'id' });
    staffStore.createIndex('restaurant_id', 'restaurant_id', { unique: false });
    staffStore.createIndex('role', 'role', { unique: false });
  }

  // Waiters
  if (!database.objectStoreNames.contains(STORES.WAITERS)) {
    const waiterStore = database.createObjectStore(STORES.WAITERS, { keyPath: 'id' });
    waiterStore.createIndex('restaurant_id', 'restaurant_id', { unique: false });
  }

  // Customers
  if (!database.objectStoreNames.contains(STORES.CUSTOMERS)) {
    const customerStore = database.createObjectStore(STORES.CUSTOMERS, { keyPath: 'id' });
    customerStore.createIndex('email', 'email', { unique: false });
    customerStore.createIndex('phone', 'phone', { unique: false });
  }

  // Loyalty
  if (!database.objectStoreNames.contains(STORES.LOYALTY)) {
    const loyaltyStore = database.createObjectStore(STORES.LOYALTY, { keyPath: 'id' });
    loyaltyStore.createIndex('customer_id', 'customer_id', { unique: false });
    loyaltyStore.createIndex('restaurant_id', 'restaurant_id', { unique: false });
  }

  // Transactions
  if (!database.objectStoreNames.contains(STORES.TRANSACTIONS)) {
    const txStore = database.createObjectStore(STORES.TRANSACTIONS, { keyPath: 'id' });
    txStore.createIndex('restaurant_id', 'restaurant_id', { unique: false });
    txStore.createIndex('date', 'date', { unique: false });
    txStore.createIndex('is_synced', 'is_synced', { unique: false });
  }

  // Daily reports
  if (!database.objectStoreNames.contains(STORES.DAILY_REPORTS)) {
    const reportStore = database.createObjectStore(STORES.DAILY_REPORTS, { keyPath: 'id' });
    reportStore.createIndex('restaurant_id', 'restaurant_id', { unique: false });
    reportStore.createIndex('date', 'date', { unique: false });
  }

  // Sync queue - for operations waiting to sync
  if (!database.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
    const syncStore = database.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
    syncStore.createIndex('type', 'type', { unique: false });
    syncStore.createIndex('priority', 'priority', { unique: false });
    syncStore.createIndex('created_at', 'created_at', { unique: false });
    syncStore.createIndex('status', 'status', { unique: false });
  }

  // Audit log - ALL offline actions
  if (!database.objectStoreNames.contains(STORES.AUDIT_LOG)) {
    const auditStore = database.createObjectStore(STORES.AUDIT_LOG, { keyPath: 'id', autoIncrement: true });
    auditStore.createIndex('action', 'action', { unique: false });
    auditStore.createIndex('entity_type', 'entity_type', { unique: false });
    auditStore.createIndex('entity_id', 'entity_id', { unique: false });
    auditStore.createIndex('timestamp', 'timestamp', { unique: false });
    auditStore.createIndex('is_synced', 'is_synced', { unique: false });
  }

  // Conflicts - for manual resolution
  if (!database.objectStoreNames.contains(STORES.CONFLICTS)) {
    const conflictStore = database.createObjectStore(STORES.CONFLICTS, { keyPath: 'id', autoIncrement: true });
    conflictStore.createIndex('entity_type', 'entity_type', { unique: false });
    conflictStore.createIndex('entity_id', 'entity_id', { unique: false });
    conflictStore.createIndex('resolved', 'resolved', { unique: false });
  }

  // Sync status
  if (!database.objectStoreNames.contains(STORES.SYNC_STATUS)) {
    database.createObjectStore(STORES.SYNC_STATUS, { keyPath: 'key' });
  }

  // Settings
  if (!database.objectStoreNames.contains(STORES.SETTINGS)) {
    database.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
  }
}

/**
 * Get database connection
 */
const getDB = async () => {
  if (!db || !isInitialized) {
    await initOfflineDB();
  }
  return db;
};

// ==================== GENERIC CRUD OPERATIONS ====================

/**
 * Save item to a store
 */
export const saveItem = async (storeName, item) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(item);

    request.onsuccess = () => resolve(item);
    request.onerror = (e) => reject(e.target.error);
  });
};

/**
 * Save multiple items to a store
 */
export const saveItems = async (storeName, items) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);

    let completed = 0;
    const errors = [];

    items.forEach(item => {
      const request = store.put(item);
      request.onsuccess = () => {
        completed++;
        if (completed === items.length) {
          if (errors.length > 0) {
            reject(errors);
          } else {
            resolve(items);
          }
        }
      };
      request.onerror = (e) => {
        errors.push(e.target.error);
        completed++;
        if (completed === items.length) {
          reject(errors);
        }
      };
    });

    if (items.length === 0) resolve([]);
  });
};

/**
 * Get item by ID
 */
export const getItem = async (storeName, id) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = (e) => reject(e.target.error);
  });
};

/**
 * Get all items from a store
 */
export const getAllItems = async (storeName) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = (e) => reject(e.target.error);
  });
};

/**
 * Get items by index
 */
export const getItemsByIndex = async (storeName, indexName, value) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);
    const request = index.getAll(value);

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = (e) => reject(e.target.error);
  });
};

/**
 * Delete item by ID
 */
export const deleteItem = async (storeName, id) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);

    request.onsuccess = () => resolve(true);
    request.onerror = (e) => reject(e.target.error);
  });
};

/**
 * Clear all items from a store
 */
export const clearStore = async (storeName) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve(true);
    request.onerror = (e) => reject(e.target.error);
  });
};

/**
 * Count items in a store
 */
export const countItems = async (storeName) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.count();

    request.onsuccess = () => resolve(request.result);
    request.onerror = (e) => reject(e.target.error);
  });
};

// ==================== AUDIT LOGGING ====================

/**
 * Log an offline action for audit trail
 */
export const logAuditAction = async (action, entityType, entityId, data, userId = null) => {
  const auditEntry = {
    action,           // CREATE, UPDATE, DELETE
    entity_type: entityType,
    entity_id: entityId,
    data_before: data.before || null,
    data_after: data.after || null,
    user_id: userId,
    timestamp: new Date().toISOString(),
    is_synced: false,
    offline_created: true
  };

  return saveItem(STORES.AUDIT_LOG, auditEntry);
};

/**
 * Get unsynced audit logs
 */
export const getUnsyncedAuditLogs = async () => {
  return getItemsByIndex(STORES.AUDIT_LOG, 'is_synced', false);
};

/**
 * Mark audit logs as synced
 */
export const markAuditLogsSynced = async (ids) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.AUDIT_LOG], 'readwrite');
    const store = transaction.objectStore(STORES.AUDIT_LOG);

    let completed = 0;
    ids.forEach(id => {
      const getRequest = store.get(id);
      getRequest.onsuccess = () => {
        const item = getRequest.result;
        if (item) {
          item.is_synced = true;
          item.synced_at = new Date().toISOString();
          store.put(item);
        }
        completed++;
        if (completed === ids.length) resolve(true);
      };
    });

    if (ids.length === 0) resolve(true);
  });
};

// ==================== SYNC QUEUE ====================

/**
 * Add operation to sync queue
 */
export const addToSyncQueue = async (operation) => {
  const queueItem = {
    type: operation.type,           // CREATE_ORDER, UPDATE_STOCK, etc.
    method: operation.method,       // POST, PUT, DELETE
    endpoint: operation.endpoint,   // API endpoint
    data: operation.data,           // Request body
    entity_type: operation.entity_type,
    entity_id: operation.entity_id,
    priority: operation.priority || 1,  // 1=high (orders), 2=medium, 3=low
    status: 'pending',
    attempts: 0,
    max_attempts: 5,
    created_at: new Date().toISOString(),
    last_attempt: null,
    error: null
  };

  return saveItem(STORES.SYNC_QUEUE, queueItem);
};

/**
 * Get pending sync queue items (sorted by priority)
 */
export const getPendingSyncQueue = async () => {
  const items = await getItemsByIndex(STORES.SYNC_QUEUE, 'status', 'pending');
  // Sort by priority (1 first) then by created_at (older first)
  return items.sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    return new Date(a.created_at) - new Date(b.created_at);
  });
};

/**
 * Update sync queue item status
 */
export const updateSyncQueueItem = async (id, updates) => {
  const item = await getItem(STORES.SYNC_QUEUE, id);
  if (item) {
    Object.assign(item, updates);
    return saveItem(STORES.SYNC_QUEUE, item);
  }
  return null;
};

/**
 * Remove from sync queue
 */
export const removeFromSyncQueue = async (id) => {
  return deleteItem(STORES.SYNC_QUEUE, id);
};

/**
 * Get sync queue count
 */
export const getSyncQueueCount = async () => {
  return countItems(STORES.SYNC_QUEUE);
};

// ==================== CONFLICTS ====================

/**
 * Add a conflict for manual resolution
 */
export const addConflict = async (conflict) => {
  const conflictEntry = {
    entity_type: conflict.entity_type,
    entity_id: conflict.entity_id,
    local_data: conflict.local_data,
    server_data: conflict.server_data,
    conflict_type: conflict.conflict_type,  // UPDATE_CONFLICT, DELETE_CONFLICT
    resolved: false,
    resolution: null,
    created_at: new Date().toISOString()
  };

  return saveItem(STORES.CONFLICTS, conflictEntry);
};

/**
 * Get unresolved conflicts
 */
export const getUnresolvedConflicts = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CONFLICTS], 'readonly');
    const store = transaction.objectStore(STORES.CONFLICTS);
    const request = store.getAll();

    request.onsuccess = () => {
      // Filter for unresolved conflicts
      const allConflicts = request.result || [];
      const unresolved = allConflicts.filter(c => !c.resolved);
      resolve(unresolved);
    };
    request.onerror = (e) => reject(e.target.error);
  });
};

/**
 * Resolve a conflict
 */
export const resolveConflict = async (id, resolution, chosenData) => {
  const conflict = await getItem(STORES.CONFLICTS, id);
  if (conflict) {
    conflict.resolved = true;
    conflict.resolution = resolution;  // 'local', 'server', 'merged'
    conflict.chosen_data = chosenData;
    conflict.resolved_at = new Date().toISOString();
    return saveItem(STORES.CONFLICTS, conflict);
  }
  return null;
};

// ==================== SYNC STATUS ====================

/**
 * Update sync status
 */
export const updateSyncStatus = async (key, value) => {
  return saveItem(STORES.SYNC_STATUS, { key, value, updated_at: new Date().toISOString() });
};

/**
 * Get sync status
 */
export const getSyncStatus = async (key) => {
  const item = await getItem(STORES.SYNC_STATUS, key);
  return item?.value || null;
};

/**
 * Get last sync time
 */
export const getLastSyncTime = async () => {
  return getSyncStatus('last_sync_time');
};

/**
 * Set last sync time
 */
export const setLastSyncTime = async () => {
  return updateSyncStatus('last_sync_time', new Date().toISOString());
};

// ==================== SETTINGS ====================

/**
 * Save setting
 */
export const saveSetting = async (key, value) => {
  return saveItem(STORES.SETTINGS, { key, value });
};

/**
 * Get setting
 */
export const getSetting = async (key) => {
  const item = await getItem(STORES.SETTINGS, key);
  return item?.value || null;
};

// Export stores for reference
export { STORES };

// Initialize on import
initOfflineDB().catch(console.error);

export default {
  initOfflineDB,
  saveItem,
  saveItems,
  getItem,
  getAllItems,
  getItemsByIndex,
  deleteItem,
  clearStore,
  countItems,
  logAuditAction,
  getUnsyncedAuditLogs,
  markAuditLogsSynced,
  addToSyncQueue,
  getPendingSyncQueue,
  updateSyncQueueItem,
  removeFromSyncQueue,
  getSyncQueueCount,
  addConflict,
  getUnresolvedConflicts,
  resolveConflict,
  updateSyncStatus,
  getSyncStatus,
  getLastSyncTime,
  setLastSyncTime,
  saveSetting,
  getSetting,
  STORES
};
